import React, { useState, useRef, useEffect } from "react";

/**
 * Floating Chatbot Component med "thinking" loading
 * - Statisk Q&A (ingen API)
 * - Sprog: Dansk / Engelsk
 * - Foruddefinerede intents med keyword matching
 * - Forslagsspørgsmål + fritekst
 * - Responsiv, bruger brandfarve #14231b
 */
const FloatingChatbot = () => {
  // UI state
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState("da"); // 'da' or 'en'
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isBotThinking, setIsBotThinking] = useState(false); // NEW: loading state
  const messagesEndRef = useRef(null);

  // --- INTENTS (static Q&A) -------------------------------------------------
  const intents = [
    {
      id: "greeting",
      patterns_da: ["hej", "god dag", "halløj", "goddag", "hejsa", "start"],
      patterns_en: [
        "hi",
        "hello",
        "hey",
        "good morning",
        "good afternoon",
        "start",
      ],
      answer_da:
        "Hej! 👋 Jeg er din kaffeassistent. Spørg mig om kaffe, bønnetyper, ristning eller vores produkter!",
      answer_en:
        "Hi! 👋 I'm your coffee assistant. Ask me about coffee, bean types, roasting, or our products!",
    },

    {
      id: "what_is_coffee",
      patterns_da: [
        "hvad er kaffe",
        "fortæl om kaffe",
        "kaffe definition",
        "hvad består kaffe af",
      ],
      patterns_en: [
        "what is coffee",
        "tell me about coffee",
        "coffee definition",
        "what is coffee made of",
      ],
      answer_da:
        "Kaffe er en drik lavet af ristede kaffebønner, som er frøene fra kaffebær. Den dyrkes omkring ækvator og gennemgår tørring, ristning og brygning før den ender i koppen. ☕",
      answer_en:
        "Coffee is a beverage made from roasted coffee beans, which are the seeds of coffee cherries. It is grown around the equator and goes through drying, roasting, and brewing before reaching your cup. ☕",
    },

    {
      id: "arabica_robusta",
      patterns_da: [
        "arabica",
        "robusta",
        "forskel arabica robusta",
        "bønnetyper",
      ],
      patterns_en: [
        "arabica",
        "robusta",
        "difference arabica robusta",
        "coffee beans types",
      ],
      answer_da:
        "Der findes primært to kaffetyper:\n• Arabica – mild, aromatisk og ofte med syrlighed\n• Robusta – kraftigere, mere bitter og med højere koffein\nArabica giver finesse, mens Robusta giver fylde og crema.",
      answer_en:
        "There are two main coffee types:\n• Arabica – smooth, aromatic, often with acidity\n• Robusta – stronger, more bitter, and higher in caffeine\nArabica gives finesse, while Robusta adds body and crema.",
    },

    {
      id: "roast_levels",
      patterns_da: ["lys rist", "mellem rist", "mørk rist", "ristningsgrad"],
      patterns_en: [
        "light roast",
        "medium roast",
        "dark roast",
        "roast levels",
      ],
      answer_da:
        "Ristning påvirker smagen:\n• Lys rist – frugtig og syrlig\n• Mellem rist – balanceret og rund\n• Mørk rist – kraftig og bitter med chokoladenoter",
      answer_en:
        "Roasting affects flavor:\n• Light roast – fruity and acidic\n• Medium roast – balanced and smooth\n• Dark roast – bold and bitter with chocolate notes",
    },

    {
      id: "our_products",
      patterns_da: [
        "jeres produkter",
        "hvad sælger i",
        "sortiment",
        "produkter",
        "hvad har i",
      ],
      patterns_en: [
        "your products",
        "what do you sell",
        "product range",
        "products",
        "what do you have",
      ],
      answer_da:
        "Vi tilbyder:\n• Qualità Rossa\n• Qualità Blu\n• Qualità Extra Bar\n• Qualità Extra Bar (Distributori)\n• Qualità Verde Bar\n• Qualità Espresso\n• 7 Origini\nSpørg ind til et produkt for mere info 👇",
      answer_en:
        "We offer:\n• Qualità Rossa\n• Qualità Blu\n• Qualità Extra Bar\n• Qualità Extra Bar (Distributori)\n• Qualità Verde Bar\n• Qualità Espresso\n• 7 Origini\nAsk about a product for more details 👇",
    },

    {
      id: "qualita_rossa",
      patterns_da: ["qualita rossa", "rossa", "100 robusta", "stærk kaffe"],
      patterns_en: [
        "qualita rossa",
        "rossa coffee",
        "100 robusta",
        "strong coffee",
      ],
      answer_da:
        "Qualità Rossa er en kraftig kaffe lavet på 100% Robusta. Den har intens fylde, høj koffein og let træagtige noter.",
      answer_en:
        "Qualità Rossa is a strong coffee made from 100% Robusta. It has intense body, high caffeine, and slightly woody notes.",
    },

    {
      id: "qualita_blu",
      patterns_da: ["qualita blu", "blu kaffe", "balanceret kaffe"],
      patterns_en: ["qualita blu", "blu coffee", "balanced coffee"],
      answer_da:
        "Qualità Blu er en harmonisk blanding af Arabica og Robusta med fyldig krop og sødmefulde chokoladenoter.",
      answer_en:
        "Qualità Blu is a harmonious blend of Arabica and Robusta with full body and sweet chocolate notes.",
    },

    {
      id: "qualita_extra_bar",
      patterns_da: ["qualita extra bar", "extra bar", "bar kaffe"],
      patterns_en: ["qualita extra bar", "extra bar coffee", "bar coffee"],
      answer_da:
        "Qualità Extra Bar er en velafbalanceret kaffe med behagelig syrlighed og vedvarende aroma – ideel til professionelle maskiner.",
      answer_en:
        "Qualità Extra Bar is a well-balanced coffee with pleasant acidity and lasting aroma – ideal for professional machines.",
    },

    {
      id: "qualita_extra_bar_dis",
      patterns_da: ["extra bar distributori", "automat kaffe", "vending kaffe"],
      patterns_en: [
        "extra bar distributori",
        "vending coffee",
        "automatic coffee",
      ],
      answer_da:
        "Qualità Extra Bar (Distributori) er udviklet til automater og giver en stabil, fyldig og balanceret smag.",
      answer_en:
        "Qualità Extra Bar (Distributori) is designed for vending machines and delivers a stable, full-bodied, and balanced taste.",
    },

    {
      id: "qualita_verde_bar",
      patterns_da: ["qualita verde bar", "verde bar", "intens kaffe"],
      patterns_en: ["qualita verde bar", "verde bar coffee", "intense coffee"],
      answer_da:
        "Qualità Verde Bar er en intens og fyldig kaffe med markant karakter – perfekt til espresso og automater.",
      answer_en:
        "Qualità Verde Bar is an intense and full-bodied coffee with strong character – perfect for espresso and machines.",
    },

    {
      id: "qualita_espresso",
      patterns_da: ["qualita espresso", "espresso kaffe", "crema kaffe"],
      patterns_en: ["qualita espresso", "espresso coffee", "crema coffee"],
      answer_da:
        "Qualità Espresso er en fyldig espressoblanding med tæt crema og lang eftersmag – ideel til både espresso og mælkedrikke.",
      answer_en:
        "Qualità Espresso is a full-bodied espresso blend with dense crema and long aftertaste – ideal for espresso and milk-based drinks.",
    },

    {
      id: "7_origini",
      patterns_da: ["7 origini", "premium kaffe", "arabica mørk"],
      patterns_en: ["7 origini", "premium coffee", "arabica dark"],
      answer_da:
        "7 Origini er en eksklusiv 100% Arabica kaffe fra syv oprindelser. Mørkristet med elegant og kompleks smag.",
      answer_en:
        "7 Origini is an exclusive 100% Arabica coffee from seven origins. Dark roasted with an elegant and complex flavor.",
    },
  ];

  // Fallback answers
  const fallback_da =
    "Det forstod jeg ikke helt. Prøv at spørge om kaffe, ristning eller vores produkter. 🙋";

  const fallback_en =
    "I didn't quite understand. Try asking about coffee, roasting, or our products. 🙋";

  // Suggested questions
  const suggested_da = [
    "Hvad er kaffe?",
    "Arabica vs Robusta",
    "Hvad er ristning?",
    "Hvilke produkter har I?",
    "Hvilken kaffe skal jeg vælge?",
  ];

  const suggested_en = [
    "What is coffee?",
    "Arabica vs Robusta",
    "What are roast levels?",
    "What products do you have?",
    "Which coffee should I choose?",
  ];

  // --- Helper: Find best matching intent ------------------------------------
  const findAnswer = (userQuestion, lang) => {
    const lowerInput = userQuestion.toLowerCase().trim();
    if (lowerInput === "") return null;

    for (const intent of intents) {
      const patterns = lang === "da" ? intent.patterns_da : intent.patterns_en;
      const match = patterns.some((pattern) =>
        lowerInput.includes(pattern.toLowerCase()),
      );
      if (match) {
        return lang === "da" ? intent.answer_da : intent.answer_en;
      }
    }
    return lang === "da" ? fallback_da : fallback_en;
  };

  // --- Add a message to the chat -------------------------------------------
  const addMessage = (text, sender) => {
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), text, sender, timestamp: new Date() },
    ]);
  };

  // --- Process user input med tænke-indikator -------------------------------
  const handleSendMessage = async (questionOverride = null) => {
    const question =
      questionOverride !== null ? questionOverride : inputText.trim();
    if (question === "" || isBotThinking) return; // Undgå multiple requests

    // Tilføj brugerbesked
    addMessage(question, "user");
    setInputText("");

    // Start "thinking"
    setIsBotThinking(true);

    // Simuler en lille forsinkelse (fx 500ms) for at efterligne "tænketid"
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Generér svar
    const answer = findAnswer(question, language);
    if (answer) {
      addMessage(answer, "bot");
    } else {
      addMessage(
        language === "da"
          ? "Prøv igen med et andet spørgsmål."
          : "Try again with another question.",
        "bot",
      );
    }

    setIsBotThinking(false);
  };

  // --- Language change: reset conversation and show welcome ----------------
  useEffect(() => {
    const welcomeMessage =
      language === "da"
        ? "Hej! Jeg er kaffeassistenten. Spørg mig om kaffe, vores produkter eller brygning. 😊"
        : "Hi! I’m the coffee assistant. Ask me about coffee, our products, or brewing. 😊";
    setMessages([{ id: Date.now(), text: welcomeMessage, sender: "bot" }]);
    setIsBotThinking(false); // Nulstil tænke-tilstand ved sprogskift
  }, [language]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // --- Handle Enter key ----------------------------------------------------
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !isBotThinking) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // --- Toggle chat window --------------------------------------------------
  const toggleChat = () => setIsOpen(!isOpen);

  // --- Komponent: Tænke-indikator (tre prikker) ---------------------------
  const ThinkingIndicator = () => (
    <div className="flex justify-start">
      <div className="bg-white border border-gray-200 text-gray-800 rounded-2xl rounded-bl-none px-4 py-2 shadow-sm">
        <div className="flex space-x-1">
          <div
            className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
            style={{ animationDelay: "0ms" }}
          ></div>
          <div
            className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
            style={{ animationDelay: "150ms" }}
          ></div>
          <div
            className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
            style={{ animationDelay: "300ms" }}
          ></div>
        </div>
      </div>
    </div>
  );

  // --- Render --------------------------------------------------------------
  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="bg-[#14231b] text-white rounded-full p-4 shadow-lg hover:bg-opacity-90 transition-all duration-200 focus:outline-none"
          aria-label="Open chat"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="bg-white rounded-2xl shadow-2xl flex flex-col w-[90vw] sm:w-[380px] h-[550px] max-h-[80vh] overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-[#14231b] text-white px-4 py-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-xl">☕</span>
              <span className="font-semibold">Scandivia Bot</span>
            </div>
            <div className="flex items-center gap-3">
              {/* Language switcher */}
              <div className="flex gap-1 bg-white/20 rounded-full p-1">
                <button
                  onClick={() => setLanguage("da")}
                  className={`px-2 py-1 text-xs rounded-full transition ${
                    language === "da"
                      ? "bg-white text-[#14231b] font-semibold"
                      : "text-white hover:bg-white/20"
                  }`}
                >
                  DK
                </button>
                <button
                  onClick={() => setLanguage("en")}
                  className={`px-2 py-1 text-xs rounded-full transition ${
                    language === "en"
                      ? "bg-white text-[#14231b] font-semibold"
                      : "text-white hover:bg-white/20"
                  }`}
                >
                  EN
                </button>
              </div>
              <button
                onClick={toggleChat}
                className="text-white hover:bg-white/20 rounded-full p-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
                    msg.sender === "user"
                      ? "bg-[#14231b] text-white rounded-br-none"
                      : "bg-white border border-gray-200 text-gray-800 rounded-bl-none shadow-sm"
                  }`}
                >
                  {msg.text.split("\n").map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < msg.text.split("\n").length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
            {/* Vis tænke-indikator hvis bot tænker */}
            {isBotThinking && <ThinkingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested questions */}
          <div className="px-4 py-2 bg-white border-t border-gray-100">
            <div className="flex flex-wrap gap-2">
              {(language === "da" ? suggested_da : suggested_en).map(
                (q, idx) => (
                  <button
                    key={idx}
                    onClick={() => !isBotThinking && handleSendMessage(q)}
                    disabled={isBotThinking}
                    className={`text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full px-3 py-1 transition ${
                      isBotThinking ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {q}
                  </button>
                ),
              )}
            </div>
          </div>

          {/* Input area */}
          <div className="p-3 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isBotThinking}
                placeholder={
                  language === "da"
                    ? "Skriv dit spørgsmål..."
                    : "Type your question..."
                }
                className={`flex-1 border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#14231b] focus:border-transparent ${
                  isBotThinking ? "bg-gray-100" : ""
                }`}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={isBotThinking}
                className={`bg-[#14231b] text-white rounded-full p-2 transition ${
                  isBotThinking
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-opacity-90"
                }`}
                aria-label="Send message"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingChatbot;
